import { FC, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import { FormProvider, useWatch } from 'react-hook-form';
import { apiUserService } from '@api';
import { EAddressType, TBusinessAddress, TIndividualAddress } from '@xeppt/xeppt-sdk/types/user';
import { useForm } from '@hooks/helpers/useForm';
import { defaultAddressData } from '@const/default_form_data';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { validator } from '@xeppt/xeppt-sdk';
import FullAddress from '@components/address';
import { useLocales } from '@hooks/helpers/useLocales';
import { Country } from 'country-state-city';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { Icon } from '@components/icons';
import useResponsive from '@hooks/helpers/useResponsive';

interface IProps {
    name: 'billing_address' | 'business_address' | 'registration_address' | 'trading_address';
    type: EAddressType;
    data: TBusinessAddress | TIndividualAddress;
}

const AddressSection: FC<IProps> = ({ name, type, data }) => {
    const [isHigh, setIsHigh] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const {
        validationLocale,
        requestSuccessLocale,
        submitLocale,
        requestErrorLocale,
        labelLocale
    } = useLocales();
    const { isMobile } = useResponsive();
    const formMethods = useForm({
        defaultValues: {
            [name]: defaultAddressData
        },
        resolver: zodResolver(
            z.object({
                [name]: validator.address({
                    address_1: validationLocale('address_1'),
                    address_2: validationLocale('address_2'),
                    zip_code: validationLocale('zip_code'),
                    city: validationLocale('city'),
                    country: validationLocale('country'),
                    region: validationLocale('region')
                })
            })
        )
    });

    const defaultData = useMemo(() => {
        return {
            [name]: {
                //@ts-ignore
                ...data?.[name],
                country:
                    Country.getAllCountries().find(
                        //@ts-ignore
                        (item) => item.isoCode === data?.[name]?.country
                        //@ts-ignore
                    )?.name || data?.[name]?.country
            }
        };
    }, [name, data]);

    useEffect(() => {
        if (data && name in data) {
            formMethods.reset(defaultData);
        }
    }, [data]);

    const address = useWatch({ name, control: formMethods.control });

    const { handleRequest: onSubmit, isLoading } = useApiMutation({
        method: () =>
            apiUserService.updateAddress(type, {
                ...address,
                country:
                    Country.getAllCountries().find((item) => item.name === address.country)
                        ?.isoCode || address.country
            }),
        onSuccess: () => {
            setIsEdit((state) => !state);
            requestSuccessLocale('update_address');
        },
        onError: requestErrorLocale
    });

    const onCancel = () => {
        setIsHigh(true);
        if (data && name in data) {
            formMethods.reset({
                [name]: {
                    //@ts-ignore
                    ...data[name]
                }
            });
        }
        setIsEdit(false);
        setTimeout(() => {
            setIsHigh(false);
        }, 0);
    };

    const dynamicTitle = useMemo(() => {
        switch (type) {
            case EAddressType.BILLING:
                return 'billing_address';
            case EAddressType.REGISTRATION:
                return 'registration_address';
            case EAddressType.TRADING:
                return 'trading_address';
            case EAddressType.BUSINESS:
                return 'business_address';
        }
    }, [type]);

    const dynamicDescription = useMemo(() => {
        switch (type) {
            case EAddressType.BILLING:
                return 'Update your billing address if necessary';
            case EAddressType.REGISTRATION:
                return 'Update the company’s registered office address if necessary';
            case EAddressType.TRADING:
                return 'Update the company’s trading address if necessary';
            case EAddressType.BUSINESS:
                return 'Update the company’s business address if necessary';
        }
    }, [type]);

    const dynamicValue = useMemo(() => {
        const value = formMethods.getValues()?.[name];
        if (value?.country && value?.zip_code && value?.address_1 && value?.region && value?.city) {
            return `${value.address_1},${value.address_2 ? ` ${value.address_2},` : ''} ${value.city}, ${value.region ? ` ${value.region},` : ''} ${value.zip_code}, ${value.country}`;
        }
        return '';
    }, [formMethods.watch()]);

    return (
        <div className={styles.container}>
            <FormProvider {...formMethods}>
                <div className={styles.header}>
                    <div className={styles.left}>
                        <div className={styles.icon}>
                            <Icon name="map_pointer" />
                        </div>
                        <div className={styles.text_wrapper}>
                            <Typography
                                className={styles.title}
                                variant="body1"
                                lh="120%"
                                weight="semibold">
                                {labelLocale(dynamicTitle)}
                            </Typography>
                            <p className={styles.description}>{dynamicDescription}</p>
                        </div>
                    </div>
                    {!isEdit && (
                        <Button
                            leftIcon={isEdit ? undefined : 'edit'}
                            variant="outlined-gold"
                            size="small"
                            onClick={() => setIsEdit(true)}>
                            {!isMobile && (isEdit ? submitLocale('save') : submitLocale('edit'))}
                        </Button>
                    )}
                </div>
                <div className={styles.content}>
                    {!isHigh && isEdit && (
                        <FullAddress name={name} isFull={isEdit} disabled={!isEdit} />
                    )}
                    {!isEdit && (
                        <div className={styles.row}>
                            <div className={`${styles.data_wrapper} ${styles.full}`}>
                                <div className={styles.description}>{dynamicValue}</div>
                            </div>
                        </div>
                    )}
                    {isEdit && (
                        <div className={styles.actions}>
                            <Button onClick={onCancel} variant="outlined-primary" size="medium">
                                {submitLocale('cancel')}
                            </Button>
                            <Button
                                isLoading={isLoading}
                                onClick={onSubmit}
                                variant="primary"
                                size="medium">
                                {submitLocale('save_changes')}
                            </Button>
                        </div>
                    )}
                </div>
            </FormProvider>
        </div>
    );
};

export default AddressSection;
